// [S-339] Perks Register — the deal-workflow command center (demo Scene 3).
// Renders the deterministic S-332 register for a deal over the S-336 routes:
// every perks line item with its cap-sheet impact + new-category flag, and an
// expandable row exposing the talent-ask/studio-offer compare, the intra-
// project equity panel, and the prior precedent. The hero leads with the real
// deterministic numbers (aggregate over-reserve exposure + slate-cascade), not
// a synthesized recommendation (that's a later drafting surface).
//
// No fake UI: the wireframe's multi-tab strip + budget-lock countdown are
// omitted because their target screens / data don't exist yet — this ships the
// register surface honestly against real seeded data.
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, PageHead } from "../components";
import {
  formatUsdShort,
  useDeal,
  useDealCapSheet,
  useDealCascade,
  useDealRegister,
  useProjectAggregate,
  type CapSheetImpact,
  type RegisterRowWire,
} from "../data/dealQuery";

type FilterKey = "all" | "cap" | "new";

export function PerksRegister(): JSX.Element {
  const { dealId = "" } = useParams<{ dealId: string }>();
  const dealQuery = useDeal(dealId);
  const registerQuery = useDealRegister(dealId);
  const capSheetQuery = useDealCapSheet(dealId);
  const cascadeQuery = useDealCascade(dealId);
  const projectQuery = useProjectAggregate(dealQuery.data?.projectId);

  const [filter, setFilter] = useState<FilterKey>("all");
  const [openRow, setOpenRow] = useState<string | null>(null);

  const rows = registerQuery.data?.rows ?? [];
  const counts = useMemo(() => tallyImpacts(rows), [rows]);
  const newCategoryCount = rows.filter((r) => r.isNewCategory).length;
  const capTouchingCount = rows.filter(
    (r) => r.capSheetImpact === "red" || r.capSheetImpact === "yellow"
  ).length;

  const filtered = rows.filter((r) => {
    if (filter === "cap") return r.capSheetImpact === "red" || r.capSheetImpact === "yellow";
    if (filter === "new") return r.isNewCategory;
    return true;
  });

  // Map party ids → names so the header + equity panel read naturally.
  const partyName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of projectQuery.data?.cast ?? []) m.set(c.id, c.name);
    return m;
  }, [projectQuery.data]);

  const projectTitle = projectQuery.data?.project.title ?? null;
  const talent = dealQuery.data ? partyName.get(dealQuery.data.talentPartyId) ?? null : null;
  const agent =
    dealQuery.data?.agentPartyId != null
      ? partyName.get(dealQuery.data.agentPartyId) ?? null
      : null;

  return (
    <div className="main__body--pr">
      <PageHead
        eyebrow="Deal workflow · perks register"
        title={
          <>
            The counter, <em>line by line.</em>
          </>
        }
        sub="Every perks line item with its cap-sheet impact, intra-project equity, and precedent — computed deterministically. Expand a row for the full analysis."
      />

      {dealId.length === 0 ? (
        <div className="pr__empty" data-testid="pr-no-deal">
          No deal selected.
        </div>
      ) : registerQuery.isLoading || dealQuery.isLoading ? (
        <div className="pr__empty" data-testid="pr-loading">
          Loading deal…
        </div>
      ) : dealQuery.isError ? (
        <Alert label="Couldn't load the deal" tone="error" data-testid="pr-error">
          {dealQuery.error?.message ?? "unknown error"}
        </Alert>
      ) : (
        <>
          <header className="pr__head" data-testid="pr-head">
            <div>
              <div className="pr__head__eyebrow">
                <span className="src-dot" /> Active deal · perks counter
              </div>
              <h1>
                {talent ?? "Untitled deal"} <em>·</em> {projectTitle ?? "—"}
              </h1>
              <div className="pr__head__meta">
                {agent !== null && (
                  <>
                    Counter from <b>{agent}</b>
                    <span className="sep">·</span>
                  </>
                )}
                <b>
                  {rows.length} line item{rows.length === 1 ? "" : "s"}
                </b>
                {dealQuery.data?.tier !== undefined && (
                  <>
                    <span className="sep">·</span>
                    {prettyTier(dealQuery.data.tier)}
                  </>
                )}
              </div>
            </div>
          </header>

          <div className="pr__hero" data-testid="pr-hero">
            <div className="pr__hero__l">
              <div className="pr__hero__eyebrow">Deterministic analysis</div>
              <h2 className="pr__hero__title">
                {counts.green} within reserve · {counts.yellow} need reallocation
                · {counts.red} over cap.
                <em>
                  {" "}
                  {newCategoryCount === 0
                    ? "No new studio categories opened."
                    : `${newCategoryCount} new categor${newCategoryCount === 1 ? "y" : "ies"} would reset precedent.`}
                </em>
              </h2>
            </div>
            <div className="pr__hero__stat">
              <div className="pr__hero__stat__lbl">If granted in full</div>
              <div className="pr__hero__stat__n" data-testid="pr-hero-exposure">
                {capSheetQuery.data
                  ? formatUsdShort(capSheetQuery.data.aggregateRedExposureUsd)
                  : "…"}
              </div>
              <div className="pr__hero__stat__sub">
                over reserve
                {capSheetQuery.data?.contingencyDrawRequired === true
                  ? " · contingency draw required"
                  : ""}
              </div>
            </div>
            <div className="pr__hero__stat">
              <div className="pr__hero__stat__lbl">Slate cascade if a new category opens</div>
              <div className="pr__hero__stat__n" data-testid="pr-hero-cascade">
                {cascadeQuery.data ? formatUsdShort(cascadeQuery.data.totalExposureUsd) : "…"}
              </div>
              <div className="pr__hero__stat__sub">
                across {cascadeQuery.data?.comparableDealCount ?? 0} deals in development
              </div>
            </div>
          </div>

          <div className="pr__ctl">
            <div className="pr__ctl__group">
              <FilterChip k="all" cur={filter} set={setFilter} label="All" n={rows.length} />
              <FilterChip
                k="cap"
                cur={filter}
                set={setFilter}
                label="Touching cap-sheet"
                n={capTouchingCount}
              />
              <FilterChip
                k="new"
                cur={filter}
                set={setFilter}
                label="New category"
                n={newCategoryCount}
              />
            </div>
          </div>

          {registerQuery.isError ? (
            <Alert label="Couldn't load the register" tone="error">
              {registerQuery.error?.message ?? "unknown error"}
            </Alert>
          ) : rows.length === 0 ? (
            <div className="pr__empty" data-testid="pr-empty">
              This deal has no line items yet.
            </div>
          ) : (
            <div className="pr__tbl" data-testid="pr-table">
              <div className="pr__tbl__hd">
                <span>Line item</span>
                <span>Category</span>
                <span>Cap-sheet</span>
                <span />
              </div>
              {filtered.map((row) => (
                <Row
                  key={row.lineItemId}
                  row={row}
                  open={openRow === row.lineItemId}
                  onToggle={() =>
                    setOpenRow(openRow === row.lineItemId ? null : row.lineItemId)
                  }
                  partyName={partyName}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({
  k,
  cur,
  set,
  label,
  n,
}: {
  k: FilterKey;
  cur: FilterKey;
  set: (k: FilterKey) => void;
  label: string;
  n: number;
}): JSX.Element {
  return (
    <Button
      variant="bare"
      className={cur === k ? "pr__ctl__chip is-on" : "pr__ctl__chip"}
      onClick={() => set(k)}
      data-testid={`pr-filter-${k}`}
    >
      {label} <span className="pr__ctl__chip__ct">{n}</span>
    </Button>
  );
}

function Row({
  row,
  open,
  onToggle,
  partyName,
}: {
  row: RegisterRowWire;
  open: boolean;
  onToggle: () => void;
  partyName: Map<string, string>;
}): JSX.Element {
  return (
    <div className={open ? "pr__row is-open" : "pr__row"} data-testid={`pr-row-${row.lineItemId}`}>
      <Button
        variant="bare"
        className="pr__row__hd"
        onClick={onToggle}
        data-testid={`pr-row-toggle-${row.lineItemId}`}
      >
        <div>
          <span className="pr__row__name">{prettySub(row.subcategoryId)}</span>
          {row.isNewCategory && (
            <span className="pr__row__name__sec pr__row__new" data-testid={`pr-new-${row.lineItemId}`}>
              new category
            </span>
          )}
        </div>
        <div className="pr__row__cat">{prettyCat(row.category)}</div>
        <div>
          <CapPill impact={row.capSheetImpact} />
        </div>
        <div className="pr__row__caret">▸</div>
      </Button>

      {open && (
        <div className="pr__row__body" data-testid={`pr-body-${row.lineItemId}`}>
          <div className="pr__bd__compare">
            <div className="pr__bd__compare__col pr__bd__compare__col--ask">
              <div className="pr__bd__compare__lbl">Talent ask</div>
              <div className="pr__bd__compare__v">{row.talentAsk}</div>
            </div>
            <div className="pr__bd__compare__col pr__bd__compare__col--offer">
              <div className="pr__bd__compare__lbl">Studio offer</div>
              <div className="pr__bd__compare__v">{row.studioOffer}</div>
            </div>
          </div>

          {row.intraProjectComparable.length > 0 && (
            <div className="pr__ipe">
              <div className="pr__ipe__lbl">
                Across the picture's cast on this perk
              </div>
              <div className="pr__ipe__grid">
                {row.intraProjectComparable.map((cell) => (
                  <div className="pr__ipe__cell" key={cell.lineItemId}>
                    <span className="pr__ipe__cell__name">
                      {dealTalentName(cell.lineItemId, partyName)}
                    </span>
                    <span className="pr__ipe__cell__v">{cell.talentAsk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pr__prec" data-testid={`pr-prec-${row.lineItemId}`}>
            {row.isNewCategory ? (
              <span className="pr__prec__new">
                No prior precedent — granting this opens a new studio category.
              </span>
            ) : (
              <span className="pr__prec__norm">
                <b>Prior precedent:</b> {row.priorPrecedent}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CapPill({ impact }: { impact: CapSheetImpact | null }): JSX.Element {
  if (impact === null) {
    return <span className="s-cap s-cap--none">—</span>;
  }
  const label = impact === "green" ? "Within reserve" : impact === "yellow" ? "Reallocate" : "Over cap";
  return (
    <span className={`s-cap s-cap--${impact[0]}`} data-testid={`pr-cap-${impact}`}>
      <span className="d" />
      {label}
    </span>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────
function tallyImpacts(rows: RegisterRowWire[]): {
  green: number;
  yellow: number;
  red: number;
} {
  return rows.reduce(
    (acc, r) => {
      if (r.capSheetImpact === "green") acc.green += 1;
      else if (r.capSheetImpact === "yellow") acc.yellow += 1;
      else if (r.capSheetImpact === "red") acc.red += 1;
      return acc;
    },
    { green: 0, yellow: 0, red: 0 }
  );
}

// The equity cell carries the comparable deal's lineItemId, not its talent —
// best-effort prettify; the panel still reads when the name isn't resolvable.
function dealTalentName(lineItemId: string, _partyName: Map<string, string>): string {
  const m = /^li-([a-z0-9]+(?:-[a-z0-9]+)*)-/.exec(lineItemId);
  return m ? prettyCat(m[1]!.replace(/-/g, " ")) : lineItemId;
}

function prettyCat(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettySub(subcategoryId: string): string {
  const tail = subcategoryId.includes(".") ? subcategoryId.split(".")[1]! : subcategoryId;
  return prettyCat(tail);
}

function prettyTier(tier: string): string {
  return prettyCat(tier);
}
